import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
} from "react";
import type {
	EFFeatureId,
	EFFeatureRuntime,
} from "@/features/runtime/FeatureTypes";
import { debugLog } from "@/utils";

export type FeatureRuntimeContextValue = EFFeatureRuntime;

const FeatureRuntimeContext =
	createContext<FeatureRuntimeContextValue | null>(null);

function createNoopUnsubscribe(): () => void {
	return () => undefined;
}

export function createDefaultFeatureRuntime(): EFFeatureRuntime {
	return {
		view: {
			host: {
				configured: false,
				gameReady: false,
				platform: "desktop",
				orientation: "landscape",
				lang: null,
				gameName: null,
				operator: null,
			},
			provider: {
				available: false,
				ready: false,
				providerId: null,
			},
			shell: {
				popupVisible: false,
				activeDockTarget: null,
			},
			selection: {
				activeFeatureId: null,
				popupFeatureId: null,
			},
		},
		actions: {
			activateFeature: (featureId: EFFeatureId) => {
				debugLog(
					"[FeatureRuntimeContext] default activateFeature() invoked before provider wiring",
					{
						featureId,
					}
				);
			},
			openFeaturePopup: (featureId: EFFeatureId) => {
				debugLog(
					"[FeatureRuntimeContext] default openFeaturePopup() invoked before provider wiring",
					{
						featureId,
					}
				);
			},
			closeFeaturePopup: () => {
				debugLog(
					"[FeatureRuntimeContext] default closeFeaturePopup() invoked before provider wiring"
				);
			},
		},
		events: {
			subscribeRewardAcquired: (): (() => void) => {
				debugLog(
					"[FeatureRuntimeContext] default subscribeRewardAcquired() invoked before provider wiring"
				);
				return createNoopUnsubscribe();
			},
		},
	};
}

export function FeatureRuntimeProvider({
	value,
	children,
}: {
	value: FeatureRuntimeContextValue;
	children: ReactNode;
}) {
	const memoizedValue = useMemo<FeatureRuntimeContextValue>(
		() => value,
		[value]
	);

	return (
		<FeatureRuntimeContext.Provider value={memoizedValue}>
			{children}
		</FeatureRuntimeContext.Provider>
	);
}

export function useFeatureRuntimeContext(): FeatureRuntimeContextValue {
	const context = useContext(FeatureRuntimeContext);

	if (!context) {
		throw new Error(
			"useFeatureRuntimeContext must be used within a FeatureRuntimeProvider"
		);
	}

	return context;
}